import {
  AlignCenter,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Loader2,
  Play,
  Trash2,
  Upload,
  Video,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Doc, Id } from "@cvx/_generated/dataModel";
import { useAction, useMutation, useQuery } from "convex/react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MyComposition } from "@/components/Remotion/Composition";
import { Player } from "@remotion/player";
import { Textarea } from "@/components/ui/textarea";
import { User } from "~/types";
import { VideoEmbed } from "@/components/ui/video-embed";
import { api } from "@cvx/_generated/api";
import { prefetch } from "remotion";
import { useNavigate } from "@tanstack/react-router";
import { useToast } from "@/components/ui/use-toast";

interface CreatorMakeVideoProps {
  campaign?: Doc<"campaigns">;
  app?: Doc<"apps">;
  user?: User | null | undefined;
}

interface UploadedVideo {
  _id: Id<"creatorUploadedVideos">;
  videoStorageId: Id<"_storage">;
  type: "hook" | "productdemo";
  url?: string | null;
}

interface ProductDemo {
  _id: Id<"_storage">;
  title: string;
  url: string | null;
}

interface Template {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  exampleVideo: string;
  explanation: string;
}

export default function CreatorMakeVideo({
  app,
  user: _user,
}: CreatorMakeVideoProps) {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State for form inputs
  const [selectedHookVideo, setSelectedHookVideo] =
    useState<UploadedVideo | null>(null);
  const [selectedProductDemoVideo, setSelectedProductDemoVideo] =
    useState<UploadedVideo | null>(null);
  console.log("selectedHookVideo", selectedHookVideo);
  console.log("selectedProductDemoVideo", selectedProductDemoVideo);
  const [hookText, setHookText] = useState("");
  const [textPosition, setTextPosition] = useState<"top" | "center" | "bottom">(
    "bottom",
  );
  const [selectedBrandProductDemo, setSelectedBrandProductDemo] =
    useState<ProductDemo | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(
    null,
  );
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);
  const [generatedVideoInfo, setGeneratedVideoInfo] = useState<{
    filename?: string;
    fileSize?: number;
    renderId?: string;
  } | null>(null);
  const [videoDurations, setVideoDurations] = useState<{
    hook?: number;
    productDemo?: number;
  }>({});
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [suggestionUrl, setSuggestionUrl] = useState("");
  const [suggestionReason, setSuggestionReason] = useState("");
  const [isSubmittingSuggestion, setIsSubmittingSuggestion] = useState(false);
  const [prefetchedVideos, setPrefetchedVideos] = useState<{
    [url: string]: { free: () => void; waitUntilDone: () => Promise<string> };
  }>({});
  const [loadingVideos, setLoadingVideos] = useState<{
    [url: string]: boolean;
  }>({});
  const [showHookSuggestions, setShowHookSuggestions] = useState(false);
  const templateExampleRef = useRef<HTMLDivElement>(null);

  // Hook suggestions data
  const hookSuggestions = [
    "app of the summer >>>>",
    "omg why am i just finding out about this app",
    "this app is literally changing my life",
    "no bc why is this actually so good",
    "POV: you find the perfect app",
    "i'm obsessed with this app rn",
    "this app hits different",
    "why didn't anyone tell me about this sooner",
    "okay but this app is actually insane",
    "screaming crying throwing up over this app",
    "this is the app we've all been waiting for",
    "not me being addicted to this app already",
    "the way this app just solved all my problems",
    "telling you about this app before it blows up",
    "this app is about to be everywhere",
  ];

  // Template data
  const templates: Template[] = [
    {
      id: "shocked-face",
      title: "Shocked Face",
      description: "React with surprise to grab attention",
      thumbnail: "😱",
      exampleVideo:
        "https://www.tiktok.com/@swiftmarket_/video/7408626978787577121",
      explanation:
        "Start with a shocked or surprised facial expression to immediately capture viewers' attention. This works great for product reveals or unexpected features.",
    },
    {
      id: "singing-along",
      title: "Singing Along",
      description: "Sing along to trending songs",
      thumbnail: "🎵",
      exampleVideo:
        "https://www.tiktok.com/@kaias_lensss/video/7376760023868902702",
      explanation:
        "Use popular songs and sing along while showcasing your product. This leverages trending audio to increase reach and engagement.",
    },
    {
      id: "pov-style",
      title: "POV Style",
      description: "React to downloading the app",
      thumbnail: "🤩",
      exampleVideo:
        "https://www.tiktok.com/@debbie.oliveira/video/7347376162965818656",
      explanation:
        "Film yourself reacting with excitement, happiness or surprise when downloading and using the app for the first time. Show genuine emotion to help viewers connect with your experience.",
    },
    {
      id: "wtf-reaction",
      title: "WTF Reaction",
      description: "React in disbelief to app features",
      thumbnail: "🤯",
      exampleVideo:
        "https://www.tiktok.com/@getpandaextract/video/7538443763883052306",
      explanation:
        "Film your genuine shocked reaction as you discover mind-blowing features, showing how the app is literally changing your life in unbelievable ways.",
    },
    {
      id: "funny-moment",
      title: "Funny Moment",
      description: "Share a hilarious app-related moment",
      thumbnail: "😂",
      exampleVideo:
        "https://www.tiktok.com/@bia.chats/video/7519347419939605774",
      explanation:
        "Create a funny, relatable moment showing an amusing or ridiculous situation that happens while using the app's features. Use humor to make your content entertaining and shareable.",
    },
    {
      id: "active-lifestyle",
      title: "Active Lifestyle",
      description: "Show the app in action during activities",
      thumbnail: "🏃‍♂️",
      exampleVideo:
        "https://www.tiktok.com/@motivationapp/video/7519894053505207607",
      explanation:
        "Film yourself using the app while doing physical activities like running, walking, or working out. Ideally, the app is somehow related to the activity.",
    },
    {
      id: "x-is-hard",
      title: "X is So Hard",
      description: "Complain about a problem your app solves",
      thumbnail: "😫",
      exampleVideo:
        "https://www.tiktok.com/@try.cal.ai/video/7453678697472691502",
      explanation:
        "Start by expressing frustration about how difficult a specific task is (that your app solves), then transition to showing how your app makes it effortless.",
    },
    {
      id: "i-wish",
      title: "I Wish...",
      description: "Express a desire your app fulfills",
      thumbnail: "✨",
      exampleVideo: "https://www.tiktok.com/@tripbff/video/7254301929277377838",
      explanation:
        "Start by saying 'I wish...' followed by a key feature or benefit of your app. Then transition to showing how your app makes that wish come true with a product demo.",
    },
    {
      id: "faceless-pov",
      title: "Faceless POV",
      description: "Film a problem-solution scenario without showing your face",
      thumbnail: "📱",
      exampleVideo:
        "https://www.tiktok.com/@studywithlisaaa/video/7471709294740704567",
      explanation:
        "Film a relatable scenario from your POV without showing your face - like struggling with a task at your desk or observing a common problem in public. Then transition to showing how your app provides the perfect solution.",
    },
    {
      id: "suggest-template",
      title: "Got More Ideas?",
      description: "Suggest a new template style",
      thumbnail: "💡",
      exampleVideo: "",
      explanation:
        "Have you seen a viral hook style that works great? Let us know and we'll consider adding it to our template library!",
    },
  ];

  // Backend queries and mutations
  const hookVideosData = useQuery(
    api.creatoruploads.getCreatorUploadedVideosByType,
    { type: "hook" },
  );
  const productDemoVideosData = useQuery(
    api.creatoruploads.getCreatorUploadedVideosByType,
    { type: "productdemo" },
  );
  const brandProductDemosData = useQuery(
    api.creatoruploads.getAppProductDemos,
    app?._id ? { appId: app._id } : "skip",
  );
  const generateUploadUrl = useMutation(api.app.generateUploadUrl);
  const uploadCreatorVideo = useMutation(api.creatoruploads.uploadCreatorVideo);
  const deleteCreatorVideo = useMutation(api.creatoruploads.deleteCreatorVideo);
  const renderVideo = useAction(api.creatoruploads.renderVideo);

  // Transform data for component use
  const hookVideos: UploadedVideo[] = hookVideosData || [];
  const productDemoVideos: UploadedVideo[] = productDemoVideosData || [];
  const isLoadingHookVideos = hookVideosData === undefined;
  const brandProductDemos: ProductDemo[] = brandProductDemosData || [];

  // Function to prefetch video
  const prefetchVideo = async (url: string) => {
    if (!url || prefetchedVideos[url] || loadingVideos[url]) return;

    setLoadingVideos((prev) => ({ ...prev, [url]: true }));

    try {
      const prefetchResult = prefetch(url);

      setPrefetchedVideos((prev) => ({
        ...prev,
        [url]: prefetchResult,
      }));

      // Wait for the video to be fully loaded
      await prefetchResult.waitUntilDone();

      console.log(`Video prefetched successfully: ${url}`);
    } catch (error) {
      console.error(`Error prefetching video ${url}:`, error);
    } finally {
      setLoadingVideos((prev) => ({ ...prev, [url]: false }));
    }
  };

  // Function to cleanup prefetched video
  const cleanupPrefetchedVideo = (url: string) => {
    if (prefetchedVideos[url]) {
      prefetchedVideos[url].free();
      setPrefetchedVideos((prev) => {
        const { [url]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  // Function to get random hook suggestions
  const getRandomSuggestions = (count = 6) => {
    const shuffled = [...hookSuggestions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Function to get video duration from URL
  const getVideoDuration = (videoUrl: string): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        resolve(video.duration);
      };

      video.onerror = () => {
        console.warn("Could not load video duration, using default");
        resolve(5); // Default to 5 seconds if can't load
      };

      video.src = videoUrl;
    });
  };

  // Calculate total duration in frames
  const calculateTotalDuration = () => {
    const hookDuration = videoDurations.hook || 0;
    const productDemoDuration = videoDurations.productDemo || 0;
    const totalSeconds = hookDuration + productDemoDuration;

    // If no videos, use default 5 seconds
    if (totalSeconds === 0) {
      return 150; // 5 seconds * 30fps
    }

    return Math.ceil(totalSeconds * 30);
  };

  // Function to fetch video with ngrok headers and create blob URL
  const createVideoBlobUrl = async (videoUrl: string) => {
    try {
      console.log("Fetching video with ngrok headers:", videoUrl);

      const response = await fetch(videoUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const videoBlob = await response.blob();
      const blobUrl = URL.createObjectURL(videoBlob);

      console.log("Created blob URL:", blobUrl);
      setVideoBlobUrl(blobUrl);

      return blobUrl;
    } catch (error) {
      console.error("Error creating video blob URL:", error);
      toast({
        title: "Video Loading Error",
        description: `Could not load video: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
      throw error;
    }
  };

  // Effect to calculate hook video duration
  useEffect(() => {
    if (selectedHookVideo?.url) {
      getVideoDuration(selectedHookVideo.url).then((duration) => {
        setVideoDurations((prev) => ({ ...prev, hook: duration }));
      });
    } else {
      setVideoDurations((prev) => ({ ...prev, hook: undefined }));
    }
  }, [selectedHookVideo?.url]);

  // Effect to calculate product demo duration
  useEffect(() => {
    const productDemoUrl =
      selectedProductDemoVideo?.url || selectedBrandProductDemo?.url;
    if (productDemoUrl) {
      getVideoDuration(productDemoUrl).then((duration) => {
        setVideoDurations((prev) => ({ ...prev, productDemo: duration }));
      });
    } else {
      setVideoDurations((prev) => ({ ...prev, productDemo: undefined }));
    }
  }, [selectedProductDemoVideo?.url, selectedBrandProductDemo?.url]);

  // Effect to prefetch hook videos when they become available
  useEffect(() => {
    hookVideos.forEach((video) => {
      if (video.url) {
        prefetchVideo(video.url);
      }
    });
  }, [hookVideos]);

  // Effect to prefetch product demo videos when they become available
  useEffect(() => {
    productDemoVideos.forEach((video) => {
      if (video.url) {
        prefetchVideo(video.url);
      }
    });
  }, [productDemoVideos]);

  // Effect to prefetch brand product demos when they become available
  useEffect(() => {
    brandProductDemos.forEach((demo) => {
      if (demo.url) {
        prefetchVideo(demo.url);
      }
    });
  }, [brandProductDemos]);

  // Effect to prefetch selected videos immediately
  useEffect(() => {
    if (selectedHookVideo?.url) {
      prefetchVideo(selectedHookVideo.url);
    }
  }, [selectedHookVideo?.url]);

  useEffect(() => {
    const productDemoUrl =
      selectedProductDemoVideo?.url || selectedBrandProductDemo?.url;
    if (productDemoUrl) {
      prefetchVideo(productDemoUrl);
    }
  }, [selectedProductDemoVideo?.url, selectedBrandProductDemo?.url]);

  // Cleanup prefetched videos on unmount
  useEffect(() => {
    return () => {
      Object.keys(prefetchedVideos).forEach((url) => {
        cleanupPrefetchedVideo(url);
      });
    };
  }, []);

  const handleDeleteVideo = async (
    videoId: string,
    videoType: "hook" | "productdemo",
  ) => {
    try {
      const confirmed = window.confirm(
        "Are you sure you want to delete this video?",
      );
      if (!confirmed) return;

      await deleteCreatorVideo({
        videoId: videoId as Id<"creatorUploadedVideos">,
      });

      // Clear selection if the deleted video was selected
      if (videoType === "hook" && selectedHookVideo?._id === videoId) {
        setSelectedHookVideo(null);
      } else if (
        videoType === "productdemo" &&
        selectedProductDemoVideo?._id === videoId
      ) {
        setSelectedProductDemoVideo(null);
      }

      toast({
        title: "Video Deleted",
        description: "The video has been successfully deleted.",
      });
    } catch (error) {
      console.error("Error deleting video:", error);
      toast({
        title: "Delete Failed",
        description:
          error instanceof Error ? error.message : "Failed to delete video.",
        variant: "destructive",
      });
    }
  };

  const handleBackToDashboard = () => {
    navigate({
      to: "/dashboard",
    });
  };

  const handleVideoUpload = async (type: "hook" | "productdemo") => {
    // Create a file input element
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "video/*";
    fileInput.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("video/")) {
        toast({
          title: "Invalid file type",
          description: "Please select a video file.",
          variant: "destructive",
        });
        return;
      }

      // Validate file size (e.g., max 100MB)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: "Please select a video file smaller than 100MB.",
          variant: "destructive",
        });
        return;
      }

      setIsUploading(true);

      try {
        // Generate upload URL
        const uploadUrl = await generateUploadUrl();

        // Upload file to storage
        const uploadResponse = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": file.type },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload file");
        }

        const { storageId } = await uploadResponse.json();

        // Save video to database
        await uploadCreatorVideo({
          videoStorageId: storageId,
          type: type,
        });

        toast({
          title: "Success",
          description: "Video uploaded successfully!",
        });
      } catch (error) {
        console.error("Upload error:", error);
        toast({
          title: "Upload failed",
          description: "Failed to upload video. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
      }
    };

    fileInput.click();
  };

  const handleTemplateClick = (template: Template) => {
    if (template.id === "suggest-template") {
      setShowSuggestionDialog(true);
    } else {
      setSelectedTemplate(template);
      // Scroll to template example section after a short delay to allow DOM update
      setTimeout(() => {
        templateExampleRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  const handleSubmitSuggestion = async () => {
    if (!suggestionUrl.trim()) {
      toast({
        title: "Missing URL",
        description: "Please provide a TikTok or Instagram link.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmittingSuggestion(true);

    try {
      const webhookData = {
        text: "New Template Suggestion",
        blocks: [
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: `*New Template Suggestion from CreatorMakeVideo*\n\n*Video URL:* ${suggestionUrl}\n\n*Reason/Additional Info:* ${suggestionReason || "No additional info provided"}`,
            },
          },
        ],
      };

      const response = await fetch(
        "https://hooks.slack.com/services/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",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(webhookData),
        },
      );

      if (response.ok) {
        toast({
          title: "Suggestion Submitted!",
          description: "Thank you for your suggestion. We'll review it soon!",
        });
        setShowSuggestionDialog(false);
        setSuggestionUrl("");
        setSuggestionReason("");
      } else {
        throw new Error("Failed to submit suggestion");
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error);
      toast({
        title: "Submission Failed",
        description: "Failed to submit your suggestion. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingSuggestion(false);
    }
  };

  const handleCreateVideo = async () => {
    if (!selectedHookVideo) {
      toast({
        title: "Missing Hook Video",
        description: "Please select a hook video to create your video.",
        variant: "destructive",
      });
      return;
    }

    if (!hookText.trim()) {
      toast({
        title: "Missing Hook Text",
        description: "Please enter hook text for your video.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsRendering(true);

      const inputProps = {
        hookVideo: selectedHookVideo.url || undefined,
        productDemo:
          selectedProductDemoVideo?.url ||
          selectedBrandProductDemo?.url ||
          undefined,
        durationInFrames: calculateTotalDuration(),
        overlayText: hookText.trim(),
        textPosition: textPosition,
        hookDuration: videoDurations.hook,
        productDemoDuration: videoDurations.productDemo,
      };

      console.log("Rendering video with:", inputProps);

      const result = await renderVideo({ inputProps });

      if (result.success && result.downloadUrl) {
        // Store the generated video URL and info for display
        setGeneratedVideoUrl(result.downloadUrl);
        setGeneratedVideoInfo({
          filename: result.filename,
          fileSize: result.fileSize,
          renderId: result.renderId,
        });

        // Create blob URL for video playback
        await createVideoBlobUrl(result.downloadUrl);

        toast({
          title: "Video Created Successfully!",
          description: `Your video has been generated and is ready for preview.`,
        });
      }
    } catch (error) {
      console.error("Error creating video:", error);
      toast({
        title: "Video Creation Failed",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsRendering(false);
    }
  };

  // Full-screen loading state during video generation
  if (isRendering) {
    return (
      <div className="flex h-screen w-full bg-secondary dark:bg-black items-center justify-center">
        <div className="text-center space-y-6 max-w-md mx-auto px-6">
          <div className="relative">
            <div className="w-20 h-20 mx-auto">
              <Loader2 className="w-20 h-20 animate-spin text-orange-500" />
            </div>
            <div className="absolute inset-0 w-20 h-20 mx-auto border-4 border-orange-200 rounded-full animate-pulse"></div>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-primary">
              Generating Your Video ✨
            </h2>
            <p className="text-primary/70 text-lg">
              This may take 2-3 minutes...
            </p>
            <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mt-4">
              <p className="text-orange-800 dark:text-orange-200 text-sm font-medium">
                ⚠️ Please don't close this tab while your video is being
                generated
              </p>
            </div>
          </div>

          <div className="space-y-2 text-sm text-primary/60">
            <p>🎬 Processing your hook video</p>
            <p>🎨 Adding text overlay</p>
            <p>🔧 Rendering final video</p>
          </div>
        </div>
      </div>
    );
  }

  // Full-screen generated video display
  if (generatedVideoUrl && videoBlobUrl) {
    return (
      <div className="flex min-h-screen w-full bg-secondary dark:bg-black items-center justify-center p-4 lg:p-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 max-w-6xl mx-auto w-full items-center justify-center">
          {/* Video Section */}
          <div className="flex-shrink-0">
            <video
              src={videoBlobUrl}
              controls
              autoPlay
              style={{
                maxWidth: "min(400px, 90vw)",
                maxHeight: "75vh",
                width: "auto",
                height: "auto",
                borderRadius: "16px",
                objectFit: "cover",
              }}
              className="bg-black shadow-2xl"
              onError={(e) => {
                console.error("Video loading error:", e);
                console.error("Video blob URL:", videoBlobUrl);
                toast({
                  title: "Video Loading Error",
                  description:
                    "The generated video couldn't be loaded. Try downloading it directly.",
                  variant: "destructive",
                });
              }}
            />
          </div>

          {/* Content Card Section */}
          <div className="flex-1 max-w-md lg:max-w-lg">
            <Card className="p-6 lg:p-8 shadow-xl">
              <div className="space-y-6">
                {/* Header */}
                <div className="space-y-3 text-center">
                  <h2 className="text-2xl lg:text-3xl font-bold text-primary">
                    Your Video is Ready! 🎉
                  </h2>
                  <p className="text-primary/70 text-base lg:text-lg">
                    Preview your generated video and download it when you're
                    happy with the result.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={async () => {
                      try {
                        const a = document.createElement("a");
                        a.href = generatedVideoUrl;
                        a.download =
                          generatedVideoInfo?.filename ||
                          `generated-video-${Date.now()}.mp4`;
                        a.target = "_blank";
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      } catch (error) {
                        console.error("Download error:", error);
                        toast({
                          title: "Download Error",
                          description: `Could not download video: ${error instanceof Error ? error.message : "Unknown error"}`,
                          variant: "destructive",
                        });
                      }
                    }}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-3"
                  >
                    Download Video
                  </Button>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                      onClick={() => {
                        if (videoBlobUrl) {
                          URL.revokeObjectURL(videoBlobUrl);
                        }
                        setGeneratedVideoUrl(null);
                        setGeneratedVideoInfo(null);
                        setVideoBlobUrl(null);
                      }}
                      variant="outline"
                      className="w-full"
                    >
                      Create Another Video
                    </Button>

                    <Button
                      onClick={handleBackToDashboard}
                      variant="outline"
                      className="w-full"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Back to Dashboard
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full bg-secondary px-4 sm:px-6 dark:bg-black overflow-auto lg:overflow-hidden">
      <div className="z-10 mx-auto flex flex-col h-full w-full max-w-screen-2xl py-4 lg:py-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-primary">
                Create Your Video
              </h1>
            </div>
          </div>

          {selectedHookVideo &&
            (selectedProductDemoVideo || selectedBrandProductDemo) && (
              <Button
                onClick={handleCreateVideo}
                disabled={
                  !selectedHookVideo || !hookText || isUploading || isRendering
                }
                className="bg-orange-500 hover:bg-orange-600 text-white w-full sm:w-auto"
                size="sm"
              >
                {isRendering ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    <span className="hidden sm:inline">
                      Generating Video...
                    </span>
                    <span className="sm:hidden">Generating...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">
                      Generate & Download Video ✨
                    </span>
                    <span className="sm:hidden">Generate & Download ✨</span>
                  </>
                )}
              </Button>
            )}
        </div>

        {/* Main Content - 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 flex-1 lg:overflow-hidden">
          {/* Left Panel - Inputs */}
          <div className="space-y-4 overflow-y-auto scrollbar-hide lg:max-h-full">
            {/* Step 1: Template Selection */}
            <Card className="p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    1
                  </div>
                  <div>
                    <Label className="text-base font-semibold">
                      Design your hook
                    </Label>
                    <p className="text-xs text-primary/60">
                      Every great video starts with an eye-grabbing hook. Time
                      to create yours ✨
                    </p>
                  </div>
                </div>

                <div className="bg-orange-100 dark:bg-orange-950/20 p-3 rounded-lg">
                  <Label className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-2 block">
                    Upload your 5-second hook
                  </Label>
                  {/* Hook Video Upload Section */}
                  {isLoadingHookVideos ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-primary/60" />
                      <p className="text-primary/60 text-sm">
                        Loading videos...
                      </p>
                    </div>
                  ) : hookVideos.length > 0 ? (
                    <div className="space-y-2">
                      <div className="grid gap-2 max-h-32 overflow-y-auto">
                        {hookVideos.map((video) => (
                          <div
                            key={video._id}
                            className={`flex items-center gap-2 p-2 border rounded-lg transition-colors relative group ${
                              selectedHookVideo?._id === video._id
                                ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20"
                                : "border-border hover:bg-muted"
                            }`}
                          >
                            <div
                              className="flex items-center gap-2 flex-1 cursor-pointer"
                              onClick={() => setSelectedHookVideo(video)}
                            >
                              <div className="w-8 h-8 bg-muted rounded flex items-center justify-center">
                                <Video className="w-4 h-4 text-primary/60" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-xs">
                                  Hook {video._id.slice(-6)}
                                </h4>
                                {video.url && (
                                  <p className="text-xs text-primary/60">
                                    {loadingVideos[video.url]
                                      ? "Loading..."
                                      : prefetchedVideos[video.url]
                                        ? "Ready to use"
                                        : "Ready to use"}
                                  </p>
                                )}
                              </div>
                              {video.url && loadingVideos[video.url] && (
                                <Loader2 className="w-3 h-3 animate-spin text-orange-500" />
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-950/20"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteVideo(video._id, "hook");
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-2">
                        <Upload className="w-6 h-6 text-primary/60" />
                      </div>
                      <p className="text-primary/60 text-sm mb-2">
                        No hook videos uploaded yet
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={() => handleVideoUpload("hook")}
                    className="w-full flex items-center gap-2 mt-2 bg-orange-500 hover:bg-orange-600 text-white"
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload your 5-second hook
                      </>
                    )}
                  </Button>
                </div>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Hook text</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-orange-500 hover:text-orange-600 text-xs h-auto p-1"
                      onClick={() =>
                        setShowHookSuggestions(!showHookSuggestions)
                      }
                    >
                      ✨ Get ideas
                    </Button>
                  </div>

                  {/* Hook Suggestions */}
                  {showHookSuggestions && (
                    <div className="mb-3 p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                      <Label className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-2 block">
                        Try these hooks:
                      </Label>
                      <div className="grid gap-2">
                        {getRandomSuggestions().map((suggestion, index) => (
                          <button
                            key={index}
                            className="text-left p-2 text-sm bg-white dark:bg-orange-950/30 rounded border border-orange-200 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                            onClick={() => {
                              setHookText(suggestion);
                              setShowHookSuggestions(false);
                            }}
                          >
                            "{suggestion}"
                          </button>
                        ))}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-2 text-orange-600 hover:text-orange-800 text-xs"
                        onClick={() => setShowHookSuggestions(false)}
                      >
                        Close suggestions
                      </Button>
                    </div>
                  )}

                  <Textarea
                    placeholder="Write down an eye-catching hook..."
                    value={hookText}
                    onChange={(e) => setHookText(e.target.value)}
                    className="min-h-20 resize-none text-sm"
                  />

                  {/* Text Position Controls - Only show when user has typed something */}
                  {hookText.trim() && (
                    <div className="space-y-2 mt-3">
                      <Label className="text-sm font-medium">
                        Text Position
                      </Label>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant={
                            textPosition === "top" ? "default" : "outline"
                          }
                          size="sm"
                          className="flex-1"
                          onClick={() => setTextPosition("top")}
                        >
                          <ChevronUp className="w-4 h-4 mr-1" />
                          Top
                        </Button>
                        <Button
                          type="button"
                          variant={
                            textPosition === "center" ? "default" : "outline"
                          }
                          size="sm"
                          className="flex-1"
                          onClick={() => setTextPosition("center")}
                        >
                          <AlignCenter className="w-4 h-4 mr-1" />
                          Center
                        </Button>
                        <Button
                          type="button"
                          variant={
                            textPosition === "bottom" ? "default" : "outline"
                          }
                          size="sm"
                          className="flex-1"
                          onClick={() => setTextPosition("bottom")}
                        >
                          <ChevronDown className="w-4 h-4 mr-1" />
                          Bottom
                        </Button>
                      </div>
                      <p className="text-xs text-primary/60">
                        This will appear as text overlay on your video
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    See examples for inspiration
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className={`flex items-center p-2 border rounded-lg cursor-pointer transition-all hover:scale-[1.02] bg-white dark:bg-gray-800 min-w-0 flex-1 basis-[calc(50%-4px)] ${
                          selectedTemplate?.id === template.id
                            ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20"
                            : "border-border hover:bg-muted"
                        }`}
                        onClick={() => handleTemplateClick(template)}
                      >
                        <div className="flex-1 flex flex-col min-w-0">
                          <h4 className="font-medium text-xs mb-1 truncate">
                            {template.title}
                          </h4>
                          <p className="text-xs text-primary/60 truncate">
                            {template.description}
                          </p>
                        </div>
                        <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center ml-2 flex-shrink-0">
                          <span className="text-lg">{template.thumbnail}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Template Example Section - Appears below step 1 when template is selected */}
            {selectedTemplate && (
              <Card
                ref={templateExampleRef}
                className="p-4 bg-orange-50 dark:bg-orange-950/10 border-orange-200 dark:border-orange-800"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="text-xl">
                        {selectedTemplate.thumbnail}
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-orange-800 dark:text-orange-200">
                          {selectedTemplate.title}
                        </h3>
                        <p className="text-xs text-orange-600 dark:text-orange-300">
                          {selectedTemplate.description}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedTemplate(null)}
                      className="h-8 w-8 p-0 text-orange-600 hover:text-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-orange-800 dark:text-orange-200">
                        Example Video
                      </Label>
                      <div className="mt-2 max-w-[280px]">
                        <VideoEmbed
                          url={selectedTemplate.exampleVideo}
                          platform="tiktok"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium text-orange-800 dark:text-orange-200">
                          How It Works
                        </Label>
                        <div className="mt-2 p-3 bg-white dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-700">
                          <p className="text-sm text-orange-700 dark:text-orange-300">
                            {selectedTemplate.explanation}
                          </p>
                        </div>
                      </div>

                      <Button
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = selectedTemplate.exampleVideo;
                          link.target = "_blank";
                          link.click();
                        }}
                        variant="outline"
                        className="w-full border-orange-300 text-orange-700 hover:bg-orange-100 dark:border-orange-600 dark:text-orange-300 dark:hover:bg-orange-900"
                        size="sm"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Watch Full Example
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Step 2: Pick or upload a Product Demo */}
            <Card className="p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    2
                  </div>
                  <div>
                    <Label className="text-base font-semibold">
                      Pick or upload a Product Demo
                    </Label>
                  </div>
                </div>

                <div className="grid gap-3">
                  {/* Brand Product Demos */}
                  {brandProductDemos.map((demo) => (
                    <div
                      key={demo._id}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all hover:scale-[1.02] bg-white dark:bg-gray-800 ${
                        selectedBrandProductDemo?._id === demo._id
                          ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20"
                          : "border-border hover:bg-muted"
                      }`}
                      onClick={() => {
                        setSelectedBrandProductDemo(demo);
                        setSelectedProductDemoVideo(null);
                      }}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{demo.title}</h4>
                      </div>
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center ml-3 flex-shrink-0">
                        {demo.url && loadingVideos[demo.url] ? (
                          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                        ) : (
                          <Play className="w-6 h-6 text-primary/60" />
                        )}
                      </div>
                    </div>
                  ))}

                  {/* User Uploaded Product Demos */}
                  {productDemoVideos.map((demo) => (
                    <div
                      key={demo._id}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all hover:scale-[1.02] bg-white dark:bg-gray-800 relative group ${
                        selectedProductDemoVideo?._id === demo._id
                          ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20"
                          : "border-border hover:bg-muted"
                      }`}
                      onClick={() => {
                        setSelectedProductDemoVideo(demo);
                        setSelectedBrandProductDemo(null);
                      }}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">
                          Demo {demo._id.slice(-6)}
                        </h4>
                      </div>
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center ml-3 flex-shrink-0">
                        {demo.url && loadingVideos[demo.url] ? (
                          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                        ) : (
                          <Video className="w-6 h-6 text-primary/60" />
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-950/20"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVideo(demo._id, "productdemo");
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}

                  {/* Upload Your Own Card */}
                  <div
                    className="flex items-center p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer transition-all hover:scale-[1.02] hover:bg-muted bg-white dark:bg-gray-800"
                    onClick={() => handleVideoUpload("productdemo")}
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">
                        {isUploading ? "Uploading..." : "Upload your own"}
                      </h4>
                    </div>
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center ml-3 flex-shrink-0">
                      {isUploading ? (
                        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                      ) : (
                        <Upload className="w-6 h-6 text-primary/60" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Panel - Video Preview */}
          <div className="flex flex-col h-full">
            <Card className="p-4 h-full flex flex-col">
              <div className="space-y-3 flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold">
                    3
                  </div>
                  <div>
                    <Label className="text-base font-semibold text-gray-500">
                      Video preview
                    </Label>
                  </div>
                </div>
                <div className="flex flex-col gap-4 items-center">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-500 italic">
                      Upload your hook and pick a demo to view and export
                    </p>
                  </div>

                  {selectedHookVideo?.url ||
                  selectedProductDemoVideo?.url ||
                  selectedBrandProductDemo?.url ? (
                    <div className="w-full max-w-[300px] mx-auto">
                      <Player
                        component={MyComposition}
                        durationInFrames={calculateTotalDuration()}
                        fps={30}
                        compositionWidth={300}
                        compositionHeight={530}
                        style={{
                          width: "100%",
                          height: "auto",
                          aspectRatio: "9/16",
                          borderRadius: "12px",
                        }}
                        controls
                        acknowledgeRemotionLicense
                        inputProps={{
                          hookVideo: selectedHookVideo?.url || undefined,
                          productDemo:
                            selectedProductDemoVideo?.url ||
                            selectedBrandProductDemo?.url ||
                            undefined,
                          durationInFrames: calculateTotalDuration(),
                          overlayText: hookText || undefined,
                          textPosition: textPosition,
                          hookDuration: videoDurations.hook,
                          productDemoDuration: videoDurations.productDemo,
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-full max-w-[300px] mx-auto aspect-[9/16] bg-muted rounded-lg flex items-center justify-center">
                      <div className="text-center space-y-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                          <Video className="w-6 h-6 text-primary/60" />
                        </div>
                        <div>
                          <p className="font-medium text-primary text-sm">
                            Video Preview
                          </p>
                          <p className="text-xs text-primary/60">
                            Select a video and add your hook to see a preview
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Template Suggestion Dialog */}
      <Dialog
        open={showSuggestionDialog}
        onOpenChange={setShowSuggestionDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-xl">💡</span>
              Suggest a Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="suggestion-url" className="text-sm font-medium">
                TikTok/Instagram Example Video URL
              </Label>
              <Input
                id="suggestion-url"
                placeholder="https://www.tiktok.com/@username/video/..."
                value={suggestionUrl}
                onChange={(e) => setSuggestionUrl(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label
                htmlFor="suggestion-reason"
                className="text-sm font-medium"
              >
                Why should we add this? (Optional)
              </Label>
              <Textarea
                id="suggestion-reason"
                placeholder="Tell us what makes this hook style effective or why it would be valuable to add..."
                value={suggestionReason}
                onChange={(e) => setSuggestionReason(e.target.value)}
                className="mt-1 min-h-20 resize-none"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSuggestionDialog(false);
                  setSuggestionUrl("");
                  setSuggestionReason("");
                }}
                disabled={isSubmittingSuggestion}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitSuggestion}
                disabled={isSubmittingSuggestion}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {isSubmittingSuggestion ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  "Submit Suggestion"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
